import { useAuth } from '@clerk/clerk-react';
import { useEffect } from 'react';

// Module-level token store. authFetch reads this; useAuthTokenSync keeps it fresh.
let _token = null;
// On-demand token getter (Clerk's getToken). Lets authFetch obtain a token even
// if useAuthTokenSync hasn't populated _token yet — fixes the race where an
// auth-required GET fires the instant isSignedIn flips, before the async token
// sync completes (caused follows / saved searches / alerts to load empty on
// refresh → appear "lost").
let _getToken = null;

export function setAuthToken(token) {
  _token = token;
}

export function setTokenGetter(fn) {
  _getToken = fn;
}

export async function authFetch(url, options = {}) {
  // Ask Clerk every time rather than trusting the cached copy.
  //
  // The old condition was `!_token` — a token was only fetched when there was
  // none, so a CACHED EXPIRED token was sent as-is and came back 401
  // "Authentication required". The 50s refresh below is not a defence:
  // browsers throttle timers in background tabs and stop them across sleep,
  // which is exactly when an admin leaves a page open and then clicks a
  // button. getToken() returns the cached token while it is still valid and
  // refreshes it when it is not, so calling it every time is cheap and always
  // correct. _token stays as the fallback for when the call itself fails.
  if (_getToken) {
    try {
      const t = await _getToken();
      if (t) _token = t;
    } catch { /* ignore — fall back to the cached token, then unauthenticated */ }
  }
  const headers = { ...(options.headers || {}) };
  if (_token) {
    headers['Authorization'] = `Bearer ${_token}`;
  }
  return fetch(url, { ...options, headers });
}

// Hook that syncs the Clerk session token into the module store.
export function useAuthTokenSync() {
  const { getToken, isSignedIn } = useAuth();
  useEffect(() => {
    let active = true;
    // Register the getter immediately so authFetch can pull a token on demand
    // even before the first async sync resolves.
    setTokenGetter(isSignedIn ? getToken : null);
    async function sync() {
      if (!isSignedIn) { setAuthToken(null); return; }
      try {
        const t = await getToken();
        if (active) setAuthToken(t);
      } catch { /* ignore */ }
    }
    sync();
    // Refresh periodically (Clerk tokens are short-lived ~60s)
    const id = setInterval(sync, 50000);
    return () => { active = false; clearInterval(id); };
  }, [isSignedIn, getToken]);
}
